import React, { useState, useEffect } from "react";
import "./DateTimeCustomizationModal.css";
import LandmarkInput from "../../components/landmarkInput/LandmarkInput";
import { parseLocalDateFromInput } from "../../helper/utils";

const DateTimeCustomizationModal = ({
  visible,
  onClose,
  onSave,
  initialEntries = [],
  // entries
}) => {
  const emptyEntry = {
    subTitle: "",
    location: "",
    landmark: "",
    landmarkCoords: null,
    date: null,
    time: null,
    useCustomAddress: false,
  };

  const [entries, setEntries] = useState(
    initialEntries.length > 0 ? initialEntries : [emptyEntry],
  );

  const [customAddress, setCustomAddress] = useState("");
  const [showCustomAddressField, setShowCustomAddressField] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (!visible) return;

    setEntries(
      initialEntries && initialEntries.length > 0
        ? initialEntries
        : [emptyEntry],
    );
  }, [visible]);

  const updateEntry = (index, field, value) => {
    const updatedEntries = [...entries];
    updatedEntries[index] = {
      ...updatedEntries[index],
      [field]: value,
    };
    setEntries(updatedEntries);

    const errorKey = `${index}_${field}`;
    if (validationErrors[errorKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  const addNewLocation = () => {
    setEntries([...entries, { ...emptyEntry }]);
  };

  const removeLocation = (index) => {
    if (entries.length > 1) {
      const updatedEntries = [...entries];
      updatedEntries.splice(index, 1);
      setEntries(updatedEntries);

      const newErrors = { ...validationErrors };
      Object.keys(newErrors).forEach((key) => {
        if (key.startsWith(`${index}_`)) {
          delete newErrors[key];
        }
      });
      setValidationErrors(newErrors);
    }
  };

  const validateEntries = () => {
    const errors = {};
    let isValid = true;

    entries.forEach((entry, index) => {
      if (!entry.subTitle || !entry.subTitle.trim()) {
        errors[`${index}_subTitle`] = "Sub Event Title is required";
        isValid = false;
      }

      if (!entry.date) {
        errors[`${index}_date`] = "Date is required";
        isValid = false;
      }

      if (!entry.time && !initialEntries[index]?.time) {
        errors[`${index}_time`] = "Time is required";
        isValid = false;
      }

      if (!entry.landmark || !entry.landmark.trim()) {
        errors[`${index}_landmark`] = "Address is required";
        isValid = false;
      }

      if (!entry.location || !entry.location.trim()) {
        errors[`${index}_location`] = "Full Address Description is required";
        isValid = false;
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const handleSave = () => {
    // console.log("Validating entries before save...");

    if (!validateEntries()) {
      // console.log("Validation failed");
      const firstErrorKey = Object.keys(validationErrors)[0];
      if (firstErrorKey) {
        const errorElement = document.querySelector(
          `[data-error="${firstErrorKey}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }
      return;
    }

    // console.log("Validation passed! Saving entries...");
    onSave(entries);
    onClose();
  };

  if (!visible) return null;

  return (
    <div className="dtc-modal-overlay" onClick={onClose}>
      <div className="dtc-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="dtc-header">
          <div className="dtc-header-bar"></div>
          <button onClick={handleSave} className="dtc-close-button">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="dtc-title-container">
          <h2 className="dtc-title">Date & Time Customization</h2>
          <p className="dtc-subtitle">
            Enter event time, date & location for each sub event.
          </p>
        </div>

        <div className="dtc-scroll-content">
          {entries.map((entry, index) => (
            <div key={index} className="dtc-location-section">
              {index > 0 && (
                <div className="dtc-location-header">
                  <h3 className="dtc-location-title">Sub Event {index + 1}</h3>
                  <button
                    className="dtc-remove-button"
                    onClick={() => removeLocation(index)}
                  >
                    <i className="fas fa-trash-2"></i>
                    <span>Remove</span>
                  </button>
                </div>
              )}

              <label className="dtc-label">
                Title <span className="create-event-screen-required">*</span>
              </label>
              <div className="dtc-input-container">
                <input
                  className={`dtc-input ${
                    validationErrors[`${index}_subTitle`]
                      ? "dtc-input-error"
                      : ""
                  }`}
                  placeholder="Enter Sub Event Title"
                  value={entry.subTitle}
                  onChange={(e) =>
                    updateEntry(index, "subTitle", e.target.value)
                  }
                  data-error={`${index}_subTitle`}
                />
              </div>
              {validationErrors[`${index}_subTitle`] && (
                <span className="dtc-error-text">
                  {validationErrors[`${index}_subTitle`]}
                </span>
              )}

              <label className="dtc-label">
                Address <span className="create-event-screen-required">*</span>
              </label>

              {!entry.useCustomAddress ? (
                <LandmarkInput
                  value={entry.landmark || ""}
                  onChangeText={(text) => {
                    updateEntry(index, "landmark", text);
                    if (text === "") {
                      updateEntry(index, "landmarkCoords", null);
                    }
                    const key = `${index}_landmark`;
                    if (validationErrors[key]) {
                      setValidationErrors((prev) => {
                        const n = { ...prev };
                        delete n[key];
                        return n;
                      });
                    }
                  }}
                  onPlaceSelected={(place) => {
                    if (!place) {
                      updateEntry(index, "landmark", "");
                      updateEntry(index, "landmarkCoords", null);
                      return;
                    }
                    updateEntry(index, "landmark", place.address);
                    if (
                      place.coords?.latitude != null &&
                      place.coords?.longitude != null
                    ) {
                      updateEntry(index, "landmarkCoords", {
                        latitude: place.coords.latitude,
                        longitude: place.coords.longitude,
                      });
                    } else {
                      updateEntry(index, "landmarkCoords", null);
                    }
                    const key = `${index}_landmark`;
                    if (validationErrors[key]) {
                      setValidationErrors((prev) => {
                        const n = { ...prev };
                        delete n[key];
                        return n;
                      });
                    }
                  }}
                  placeholder="Type your address here..."
                />
              ) : (
                <input
                  type="text"
                  className="dtc-input-field"
                  placeholder="Enter custom address"
                  value={entry.landmark || ""}
                  onChange={(e) => {
                    updateEntry(index, "landmark", e.target.value);
                    updateEntry(index, "landmarkCoords", null);
                  }}
                />
              )}

              {validationErrors[`${index}_landmark`] && (
                <span className="dtc-error-text">
                  {validationErrors[`${index}_landmark`]}
                </span>
              )}

              <button
                className="dtc-custom-address-button"
                type="button"
                onClick={() => {
                  const newValue = !entry.useCustomAddress;
                  updateEntry(index, "useCustomAddress", newValue);
                  if (newValue) {
                    updateEntry(index, "landmarkCoords", null);
                  }
                }}
              >
                {entry.useCustomAddress
                  ? "Use Google Maps"
                  : "Use Custom Address"}
              </button>

              <label className="dtc-label">
                Full Address Description{" "}
                <span className="create-event-screen-required">*</span>
              </label>
              <div className="dtc-input-container">
                <input
                  className={`dtc-input ${
                    validationErrors[`${index}_location`]
                      ? "dtc-input-error"
                      : ""
                  }`}
                  placeholder="Enter Event Location Venue"
                  value={entry.location}
                  onChange={(e) =>
                    updateEntry(index, "location", e.target.value)
                  }
                  data-error={`${index}_location`}
                />
                <div className="dtc-location-icon">
                  <i className="fas fa-map-pin"></i>
                </div>
              </div>
              {validationErrors[`${index}_location`] && (
                <span className="dtc-error-text">
                  {validationErrors[`${index}_location`]}
                </span>
              )}

              <div className="dtc-date-time-container">
                <div className="dtc-date-container">
                  <label className="dtc-label">
                    Date <span className="create-event-screen-required">*</span>
                  </label>
                  <input
                    type="date"
                    className={`dtc-date-input ${
                      validationErrors[`${index}_date`] ? "dtc-input-error" : ""
                    }`}
                    value={
                      entry.date
                        ? (() => {
                            const dateObj = new Date(entry.date);
                            const year = dateObj.getFullYear();
                            const month = String(
                              dateObj.getMonth() + 1,
                            ).padStart(2, "0");
                            const day = String(dateObj.getDate()).padStart(
                              2,
                              "0",
                            );
                            return `${year}-${month}-${day}`;
                          })()
                        : ""
                    }
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) =>
                      updateEntry(
                        index,
                        "date",
                        parseLocalDateFromInput(e.target.value),
                      )
                    }
                  />
                  {validationErrors[`${index}_date`] && (
                    <span className="dtc-error-text">
                      {validationErrors[`${index}_date`]}
                    </span>
                  )}
                </div>

                <div className="dtc-time-container">
                  <label className="dtc-label">
                    Time <span className="create-event-screen-required">*</span>
                  </label>
                  <input
                    type="time"
                    className={`dtc-time-input ${
                      validationErrors[`${index}_time`] ? "dtc-input-error" : ""
                    }`}
                    value={
                      entry.time
                        ? typeof entry.time === "string"
                          ? (() => {
                              const timeMatch = entry.time.match(
                                /(\d{1,2}):(\d{2})\s*(AM|PM)/i,
                              );
                              if (timeMatch) {
                                let hours = parseInt(timeMatch[1], 10);
                                const minutes = timeMatch[2];
                                const period = timeMatch[3].toUpperCase();

                                if (period === "PM" && hours !== 12)
                                  hours += 12;
                                if (period === "AM" && hours === 12) hours = 0;

                                return `${String(hours).padStart(
                                  2,
                                  "0",
                                )}:${minutes}`;
                              }
                              return entry.time;
                            })()
                          : new Date(entry.time).toTimeString().slice(0, 5)
                        : ""
                    }
                    onClick={(e) => e.target.showPicker()}
                    onChange={(e) => {
                      const [hours, minutes] = e.target.value.split(":");
                      const date = new Date();
                      date.setHours(Number.parseInt(hours, 10));
                      date.setMinutes(Number.parseInt(minutes, 10));
                      date.setSeconds(0);
                      date.setMilliseconds(0);
                      updateEntry(index, "time", date);
                    }}
                    data-error={`${index}_time`}
                  />
                  {validationErrors[`${index}_time`] && (
                    <span className="dtc-error-text">
                      {validationErrors[`${index}_time`]}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}

          <div className="dtc-add-more-section">
            <h3 className="dtc-add-more-title">Add More</h3>
            <p className="dtc-add-more-subtitle">
              You have the liberty to add as many as possible
            </p>
            <button className="dtc-add-button" onClick={addNewLocation}>
              <i className="fas fa-plus"></i>
              <span>Add</span>
            </button>
          </div>
        </div>

        <div className="dtc-button-container">
          <button className="dtc-back-button" onClick={onClose}>
            Back
          </button>
          <button className="dtc-save-button" onClick={handleSave}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default DateTimeCustomizationModal;
